import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"
import { fmtDate, isoDate, parseIso } from "@/lib/dates";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// The API returns timestamps as ISO strings. Rendering one straight into JSX put
// "2026-08-19T13:07:07.551803+00:00" on screen next to dates that were formatted.
// Built by hand on fmtDate, not toLocaleString: the month there comes from the browser's
// own locale data ("Sept" on current engines, "Sep" on older ones), so the bell read
// "02 Sept 2026, 05:53 am" above rows reading "1 Sep 2026" and no setting could make the
// two agree. A bare date stays a date — new Date() read it as UTC midnight and printed a
// time nobody recorded, on the day before anywhere west of UTC.
export function formatDateTime(value) {
  if (!value) return "";
  if (parseIso(value)) return fmtDate(value);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const h = d.getHours() % 12 || 12;
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${fmtDate(isoDate(d))}, ${h}:${mm} ${d.getHours() < 12 ? "am" : "pm"}`;
}

// Joins the parts of a meta line, dropping the ones with nothing in them. Interpolating
// blanks directly produced dangling separators — "2026-08-18 · · Unit 1" — and labels
// with no value after them.
export function joinMeta(...parts) {
  return parts.filter((p) => p !== null && p !== undefined && String(p).trim() !== "").join(" · ");
}

// "1 certificates issued" was what the toast said after issuing one.
export function plural(count, singular, pluralForm) {
  return `${count} ${count === 1 ? singular : pluralForm || singular + "s"}`;
}

// Times come back from Postgres as HH:MM:SS, and an <input type="time"> with a seconds
// spinner sends HH:MM:SS too. Everything a person reads wants HH:MM.
export function hhmm(t) {
  const v = String(t || "");
  return /^\d{2}:\d{2}/.test(v) ? v.slice(0, 5) : v;
}

// Hours between two HH:MM times, to one decimal. Null when it cannot be answered, so a
// caller can tell "not enough information" from "zero hours".
export function hoursBetween(start, end) {
  const mins = (t) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ""));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const a = mins(start), b = mins(end);
  if (a == null || b == null || b <= a) return null;
  return Math.round(((b - a) / 60) * 10) / 10;
}
